<template lang="pug">
section.knowledge-base-settings
    .not-configured(v-if="!knowledgeSource")
        p {{ $t('No knowledge source configured for this CMS.') }}

    .grid(v-else)
        .left-panel
            table.summary.bordered-table.panel
                tr
                    td {{ $t('Sync Status') }}
                    td.col-right
                        el-tag(:type="getSyncStatusType(knowledgeSource.sync_status)" size="small")
                            | {{ $t('sync_status.' + knowledgeSource.sync_status) }}
                tr
                    td {{ $t('Documents') }}
                    td.col-right {{ knowledgeSource.document_count || 0 }}
                tr
                    td.sub {{ $t('CMS Content') }}
                    td.col-right {{ knowledgeSource.cms_document_count || 0 }}
                tr
                    td.sub-2 {{ $t('Pages') }}
                    td.col-right {{ knowledgeSource.pages_document_count || 0 }}
                tr(v-if="hasShop")
                    td.sub-2 {{ $t('Products') }}
                    td.col-right {{ knowledgeSource.products_document_count || 0 }}
                tr(v-if="hasEventManager")
                    td.sub-2 {{ $t('Events') }}
                    td.col-right {{ knowledgeSource.events_document_count || 0 }}
                tr(v-if="hasBusinessCard")
                    td.sub-2 {{ $t('Business Cards') }}
                    td.col-right {{ knowledgeSource.business_cards_document_count || 0 }}
                tr(v-if="hasRestrictedAreaOrShop")
                    td.sub-2 {{ $t('Public Addresses') }}
                    td.col-right {{ knowledgeSource.public_addresses_document_count || 0 }}
                tr
                    td.sub-2 {{ $t('Manual Entries') }}
                    td.col-right {{ knowledgeSource.manual_document_count || 0 }}
                tr
                    td.sub {{ $t('External') }}
                    td.col-right {{ knowledgeSource.external_document_count || 0 }}
                tr
                    td {{ $t('Last Synced') }}
                    td.col-right
                        span(v-if="knowledgeSource.last_synced_at") {{ formatDate(knowledgeSource.last_synced_at) }}
                        span(v-else) -

        .details
            .settings-blocks
                .indexable-types
                    h4 {{ $t('Indexed Content') }}
                    el-checkbox-group(v-model="knowledgeSource.indexable_types")
                        el-checkbox(v-for="option in indexableTypeOptions" :key="option.value" :label="option.value") {{ $t(option.label) }}

                .enabled-tools(v-if="hasAnyTools")
                    h4 {{ $t('Tools') }}
                    el-checkbox-group(v-model="knowledgeSource.enabled_tools")
                        el-checkbox(v-for="option in toolOptions" :key="option.value" :label="option.value") {{ $t(option.label) }}

                //- One research tool, one switch per feature: on for texts, off for the chatbot by default
                .web-research(v-if="hasChatbot || hasAiContent")
                    h4 {{ $t('Web research') }}
                    p.description {{ $t('The AI may research facts on the web. Each search costs 0.06 €.') }}
                    el-switch(v-if="hasAiContent" v-model="knowledgeSource.web_research_in_texts" :active-text="$t('Web research for AI content')")
                    el-switch(v-if="hasChatbot" v-model="knowledgeSource.web_research_in_chatbot" :active-text="$t('Web research in the chatbot')")

            .webhook-info.mb-6
                h4 {{ $t('Webhook Endpoint') }}
                p.description {{ $t('External systems can push documents to the knowledge base via this endpoint. Authenticate with a CMS API token (Settings > APIs).') }}
                .webhook-url-container
                    code.webhook-url {{ webhookUrl }}
                    el-button.copy-button(type="text" @click="copyWebhookUrl")
                        i(:class="copied ? 'el-icon-check' : 'el-icon-document-copy'")
</template>

<i18n>
{
    "en": {
        "sync_status.idle": "Ready",
        "sync_status.syncing": "Syncing",
        "sync_status.error": "Error",
        "sync_status.never": "Never synced"
    },
    "de": {
        "No knowledge source configured for this CMS.": "Keine Wissensquelle für dieses CMS konfiguriert.",
        "Sync Status": "Sync-Status",
        "Documents": "Dokumente",
        "CMS Content": "CMS-Inhalte",
        "External": "Extern",
        "Last Synced": "Zuletzt synchronisiert",
        "Indexed Content": "Indexierte Inhalte",
        "Pages": "Seiten",
        "Products": "Produkte",
        "Events": "Veranstaltungen",
        "Business Cards": "Visitenkarten",
        "Public Addresses": "Öffentliche Adressen",
        "Tools": "Tools",
        "Current Page": "Aktuelle Seite",
        "Current Product": "Aktuelles Produkt",
        "Product Details": "Produktdetails",
        "Product List": "Produktliste",
        "Shopping Cart": "Warenkorb",
        "Wishlist": "Wunschliste",
        "User Location": "Standort",
        "Date & Time": "Datum & Uhrzeit",
        "Geocoding": "Geocoding",
        "Webhook Endpoint": "Webhook-Endpunkt",
        "External systems can push documents to the knowledge base via this endpoint. Authenticate with a CMS API token (Settings > APIs).": "Externe Systeme können über diesen Endpunkt Dokumente an die Wissensdatenbank senden. Authentifizierung mit einem CMS-API-Token (Einstellungen > APIs).",
        "sync_status.idle": "Bereit",
        "sync_status.syncing": "Synchronisierung",
        "sync_status.error": "Fehler",
        "sync_status.never": "Nie synchronisiert",
        "Manual Entries": "Manuelle Einträge",
        "Web research": "Websuche",
        "The AI may research facts on the web. Each search costs 0.06 €.": "Die KI darf Fakten im Web recherchieren. Jede Suche kostet 0,06 €.",
        "Web research for AI content": "Websuche für KI-Inhalte",
        "Web research in the chatbot": "Websuche im Chatbot"
    }
}
</i18n>

<script>
import _ from 'lodash';
import echo from '@/echo';
import { timestamp2date } from '@/filters';
import { useSettingsStore, useSetupStore } from '@/stores';
import knowledgeSourceApi from '@/extensions/knowledge-base/store/api/knowledgeSource';

export default {
    name: 'KnowledgeBaseSettings',
    data() {
        return {
            copied: false,
        };
    },
    computed: {
        activeCms() {
            return useSetupStore().activeCms;
        },
        form() {
            return useSettingsStore().form;
        },
        knowledgeSource() {
            const knowledgeSource = _.get(this.form, 'knowledgeSource');

            // null means "all" to the backend: shown as everything ticked, so
            // saving the screen unchanged keeps everything instead of emptying it.
            if (knowledgeSource) {
                if (!Array.isArray(knowledgeSource.enabled_tools)) {
                    knowledgeSource.enabled_tools = _.map(this.toolOptions, 'value');
                }
                if (!Array.isArray(knowledgeSource.indexable_types)) {
                    knowledgeSource.indexable_types = _.map(this.indexableTypeOptions, 'value');
                }
            }

            return knowledgeSource;
        },
        indexableTypeOptions() {
            return _.filter(
                [
                    { value: 'pages', label: 'Pages', offered: true },
                    { value: 'products', label: 'Products', offered: this.hasShop },
                    { value: 'events', label: 'Events', offered: this.hasEventManager },
                    { value: 'business_cards', label: 'Business Cards', offered: this.hasBusinessCard },
                    { value: 'public_addresses', label: 'Public Addresses', offered: this.hasRestrictedAreaOrShop },
                    { value: 'manual_entries', label: 'Manual Entries', offered: true },
                ],
                'offered'
            );
        },
        toolOptions() {
            return _.filter(
                [
                    { value: 'get_current_page', label: 'Current Page', offered: this.hasChatbot && this.hasTemplateFeature('chatbot:tool:current-page') },
                    { value: 'get_current_product', label: 'Current Product', offered: this.hasChatbot && this.hasShop && this.hasTemplateFeature('chatbot:tool:current-product') },
                    { value: 'get_product_details', label: 'Product Details', offered: this.hasShop && this.hasTemplateFeature('chatbot:tool:current-product') },
                    { value: 'list_products', label: 'Product List', offered: this.hasShop && this.hasTemplateFeature('chatbot:tool:list-products') },
                    { value: 'get_cart', label: 'Shopping Cart', offered: this.hasChatbot && this.hasShop && this.hasTemplateFeature('chatbot:tool:cart') },
                    { value: 'get_wishlist', label: 'Wishlist', offered: this.hasChatbot && this.hasShop && this.hasTemplateFeature('chatbot:tool:wishlist') },
                    { value: 'get_user_location', label: 'User Location', offered: this.hasChatbot && this.hasTemplateFeature('chatbot:tool:user-location') },
                    { value: 'get_current_datetime', label: 'Date & Time', offered: this.hasChatbot && this.hasTemplateFeature('chatbot:tool:current-datetime') },
                    { value: 'geocode_location', label: 'Geocoding', offered: this.hasTemplateFeature('chatbot:tool:geocoding') },
                ],
                'offered'
            );
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions || [];
        },
        templateFeatures() {
            return _.get(this.activeCms, 'config.template.features') || [];
        },
        hasShop() {
            return this.enabledExtensions.includes('shop');
        },
        hasEventManager() {
            return this.enabledExtensions.includes('event-manager');
        },
        hasBusinessCard() {
            return this.enabledExtensions.includes('business-card');
        },
        hasRestrictedAreaOrShop() {
            return this.enabledExtensions.includes('restricted-area') || this.enabledExtensions.includes('shop');
        },
        hasChatbot() {
            return this.enabledExtensions.includes('chatbot');
        },
        hasAiContent() {
            return useSetupStore().activeExtensions.includes('ai-content');
        },
        hasAnyTools() {
            return this.templateFeatures.some((feature) => feature.startsWith('chatbot:tool:'));
        },
        webhookUrl() {
            const domain = _.get(this.activeCms, 'domain') || window.location.origin;
            return `${domain}/webhooks/knowledge-base`;
        },
    },
    methods: {
        formatDate(timestamp) {
            return timestamp2date(timestamp, this.$t('yyyy-MM-dd') + ', HH:mm');
        },
        hasTemplateFeature(featureKey) {
            return this.templateFeatures.includes(featureKey);
        },
        copyWebhookUrl() {
            navigator.clipboard.writeText(this.webhookUrl);
            this.copied = true;
            setTimeout(() => {
                this.copied = false;
            }, 2000);
        },
        getSyncStatusType(status) {
            const syncStatusTypes = {
                idle: 'success',
                syncing: 'warning',
                error: 'danger',
            };
            return syncStatusTypes[status] || 'info';
        },
        subscribeToCmsChannel() {
            const cmsId = _.get(this.activeCms, 'id');
            if (!cmsId) return;

            echo.client.private('cms.' + cmsId).listen('.knowledge-base:sync-completed', (event) => {
                if (!this.knowledgeSource) return;
                this.$set(this.knowledgeSource, 'sync_status', event.syncStatus);
                this.$set(this.knowledgeSource, 'last_synced_at', event.lastSyncedAt);
                this.$set(this.knowledgeSource, 'document_count', event.documentCount);
                this.$set(this.knowledgeSource, 'cms_document_count', event.cmsDocumentCount);
                this.$set(this.knowledgeSource, 'external_document_count', event.externalDocumentCount);
            });
        },
        /**
         * The counts are not part of the CMS the app bootstrapped with - they
         * are eight Qdrant round-trips each time and FetchSetup must never wait
         * on those - so this panel asks for them itself when it opens.
         *
         * A failure is swallowed on purpose: the counts are a read-out, and
         * everything else on this screen stays usable without them.
         */
        loadDocumentCounts() {
            const cmsId = _.get(this.activeCms, 'id');
            if (!cmsId || !this.knowledgeSource) return;

            knowledgeSourceApi
                .fetchKnowledgeSource(cmsId)
                .then((knowledgeSource) => {
                    if (!knowledgeSource || !this.knowledgeSource) return;

                    _.each(
                        [
                            'document_count',
                            'cms_document_count',
                            'external_document_count',
                            'pages_document_count',
                            'products_document_count',
                            'events_document_count',
                            'business_cards_document_count',
                            'public_addresses_document_count',
                            'manual_document_count',
                        ],
                        (field) => {
                            this.$set(this.knowledgeSource, field, _.get(knowledgeSource, field, 0));
                        }
                    );
                })
                .catch(() => {});
        },
    },
    watch: {
        // The settings form usually arrives after this panel mounts (a direct
        // load of /settings/knowledge-base), and a save reloads it without the
        // counts: load them whenever a source object arrives.
        knowledgeSource: {
            handler(knowledgeSource, previousKnowledgeSource) {
                if (knowledgeSource && knowledgeSource !== previousKnowledgeSource) this.loadDocumentCounts();
            },
            immediate: true,
        },
    },
    mounted() {
        this.subscribeToCmsChannel();
    },
    beforeDestroy() {
        const cmsId = _.get(this.activeCms, 'id');
        if (cmsId) {
            echo.client.private('cms.' + cmsId).stopListening('.knowledge-base:sync-completed');
        }
    },
};
</script>

<style lang="sass" scoped>
.knowledge-base-settings
    min-height: 200px
    .not-configured
        padding: 40px
        text-align: center
        color: #909399

.left-panel
    display: flex
    flex-direction: column
    gap: 1rem

.grid
    display: grid
    grid-template-columns: minmax(0, 1fr)
    gap: 2rem
    +res(lg)
        grid-template-columns: 320px minmax(0, 1fr)
        align-items: start
    .details
        margin-left: 0 !important

// The settings sit side by side and fill the width; a block wraps below when its column would get narrower than 260px.
.settings-blocks
    display: grid
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr))
    gap: 2rem 3rem
    align-items: start
    margin-bottom: 2rem

.indexable-types,
.enabled-tools,
.web-research
    h4
        margin: 0 0 8px
        font-size: 0.875rem
        font-weight: 600
    .el-checkbox
        display: block
        margin-left: 0
        margin-bottom: 4px
    .el-switch
        display: flex
        margin-bottom: 8px
    .description
        color: #909399
        font-size: 0.875rem
        margin: 0 0 8px

// Nine tools stacked in one column are taller than everything around them: wrap them into columns.
.enabled-tools
    .el-checkbox-group
        display: grid
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr))
        column-gap: 1rem

td.sub
    padding-left: 25px !important
    font-size: 0.875rem
    color: #909399

td.sub-2
    padding-left: 45px !important
    font-size: 0.875rem
    color: #909399

table.bordered-table.panel
    font-size: 14px
    width: 100%
    place-self: flex-start
    background: $color-body
    border-color: transparent
    border-radius: 4px
    td
        border-color: transparent
        padding: 6px 15px
        white-space: nowrap
    tr:first-of-type
        td
            padding-top: 10px
    tr:last-of-type
        td
            padding-bottom: 10px

.webhook-info
    .description
        color: #909399
        font-size: 0.875rem
        margin: 0 0 12px

.webhook-url-container
    display: flex
    align-items: center
    gap: 4px
    .webhook-url
        font-size: 0.875rem
        padding: 4px 8px
        background: #f5f7fa
        border: 1px solid #e4e7ed
        border-radius: 3px
        word-break: break-all
    .copy-button
        padding: 4px
        font-size: 0.875rem
        color: #909399

.details
    h4
        margin: 0 0 8px
        font-size: 1rem
        font-weight: 600

    .description
        color: #909399
        font-size: 0.875rem
        margin: 0 0 16px
</style>
